/**
 * ルーティング集約。
 *
 * 画面を追加するときは:
 *   1. src/features/<domain>/routes.tsx で `export const routes: RouteObject[]` を定義
 *      （パスは `/years/:year/` からの相対。例: 'vessel-registrations'）
 *   2. 下の FEATURE_ROUTES に 1 行追加
 *   3. src/navigation.ts の該当項目から `status: 'planned'` を外す
 */
import { Outlet, type RouteObject } from 'react-router-dom';
import { RequireAuth } from '@/auth/RequireAuth';
import { LoginPage } from '@/auth/LoginPage';
import { FiscalYearProvider } from '@/fiscal-year/FiscalYearProvider';
import { AppLayout } from '@/layout/AppLayout';
import { RouterErrorPage } from '@/layout/RouteErrorBoundary';
import type { RouteHandle } from '@/layout/routeHandle';
import { fiscalYearLabel } from '@/lib/wareki';
import { RootRedirect } from '@/features/system/RootRedirect';
import { PlannedPage } from '@/features/system/PlannedPage';
import { NotFoundPage } from '@/features/system/NotFoundPage';
import { findNavItem } from '@/navigation';

import { routes as portalRoutes } from '@/features/portal/routes';
import { routes as adminUsersRoutes } from '@/features/admin-users/routes';
import { routes as devCatalogRoutes } from '@/features/dev-catalog/routes';
import { routes as mastersRoutes } from '@/features/masters/routes';
import { routes as vesselsRoutes } from '@/features/vessels/routes';
import { routes as equipmentRoutes } from '@/features/equipment/routes';
import { routes as adminRoutes } from '@/features/admin/routes';
import { routes as advanceNoticesRoutes } from '@/features/advance-notices/routes';
import { routes as fixedReportsRoutes } from '@/features/fixed-reports/routes';
import { routes as voyageReportsRoutes } from '@/features/voyage-reports/routes';
import { routes as expensesRoutes } from '@/features/expenses/routes';
import { routes as seaMapRoutes } from '@/features/sea-map/routes';
import { routes as catchRoutes } from '@/features/catch/routes';
import { routes as formsRoutes } from '@/features/forms/routes';
import { routes as reportJobsRoutes } from '@/features/report-jobs/routes';
import { routes as securityRoutes } from '@/features/security/routes';
import { MfaSetupPage, MfaVerifyPage } from '@/features/security/MfaLoginPages';
// ↑ feature の routes を import（アルファベット順）

/** 年度配下（/years/:year/...）に並ぶ feature ルート */
export const FEATURE_ROUTES: RouteObject[] = [
  ...portalRoutes,
  ...adminUsersRoutes,
  ...mastersRoutes,
  ...vesselsRoutes,
  ...equipmentRoutes,
  ...adminRoutes,
  ...advanceNoticesRoutes,
  ...fixedReportsRoutes,
  ...voyageReportsRoutes,
  ...expensesRoutes,
  ...seaMapRoutes,
  ...catchRoutes,
  ...formsRoutes,
  ...reportJobsRoutes,
  ...securityRoutes,
  // ↑ ここに追加
  // 開発用カタログ（本番ビルドでは tree-shaking で除去される）
  ...(import.meta.env.DEV ? devCatalogRoutes : []),
];

export const appRoutes: RouteObject[] = [
  {
    errorElement: <RouterErrorPage />,
    element: <Outlet />,
    children: [
      { path: '/login', element: <LoginPage /> },
      // 2段階認証（ID・パスワード確認後の確認待ちセッションで表示）
      { path: '/login/mfa', element: <MfaVerifyPage /> },
      { path: '/login/mfa-setup', element: <MfaSetupPage /> },
      {
        path: '/',
        element: (
          <RequireAuth>
            <RootRedirect />
          </RequireAuth>
        ),
      },
      {
        path: '/years/:year',
        handle: { crumb: (p) => `ポータル（${fiscalYearLabel(Number(p.year))}）` } satisfies RouteHandle,
        element: (
          <RequireAuth>
            <FiscalYearProvider>
              <AppLayout />
            </FiscalYearProvider>
          </RequireAuth>
        ),
        children: [
          ...FEATURE_ROUTES,
          {
            path: '*',
            element: <PlannedPage />,
            handle: { crumb: (p) => findNavItem(p['*'] ?? '')?.item.label ?? 'ページが見つかりません' } satisfies RouteHandle,
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
